import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, Input, message, Modal, Pagination, Space, Switch, Table, Tag, Tooltip } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import request from '../../../../utils/request';

/**
 * IOC 正式拣货组件（PICK / pick_mode=ioc）
 *
 * 说明：
 * - 本组件是“历史指令执行结果 IOC”拣货的正式实现入口；
 * - 业务口径为“整容器拣出”，不支持从 IOC 容器中部分拣出内容；
 * - 与库存拣货组件（PickStepRenderer）并行存在，二者按后端下发的 pick_mode 分流。
 */
type IocPickStepRendererProps = {
  step: any;
  data: any;
  metrics: Record<string, any>;
  onChange: (key: string, val: any) => void;
  onSubmit: () => void;
  instructionCodeFromParent?: string;
  canOperate?: boolean;
  taskId?: number | null;
  stageNo?: number | null;
  executionSessionId?: string;
};

const IocPickStepRenderer: React.FC<IocPickStepRendererProps> = ({
  step,
  data,
  onSubmit,
  instructionCodeFromParent,
  canOperate,
  taskId,
  stageNo,
  executionSessionId,
}) => {
  const isReadonly = canOperate === false;
  const [hideCompleted, setHideCompleted] = useState<boolean>(true);
  const [leftPage, setLeftPage] = useState<number>(1);
  const [leftPageSize, setLeftPageSize] = useState<number>(20);
  const [logPage, setLogPage] = useState<number>(1);
  const [logPageSize, setLogPageSize] = useState<number>(20);
  const base = '/api/v1/instruction/exec/tasks';
  const rows = Array.isArray(data?.pick_rows) ? data.pick_rows : [];
  const [enrichedRows, setEnrichedRows] = useState<any[]>([]);
  const scanRef = useRef<any>(null);
  const [scanText, setScanText] = useState<string>('');
  const [pickLogs, setPickLogs] = useState<Array<{
    id: string;
    container_code: string;
    target_code: string;
    qty: number;
    operated_at: string;
    off_shelf?: boolean;
  }>>([]);

  const normalizeIocCode = (v: any) => String(v || '').trim().toUpperCase();
  useEffect(() => {
    let cancelled = false;
    const loadIocRows = async () => {
      const baseRows = (rows || []).map((r: any, idx: number) => ({
        ...r,
        row_id: r?.row_id || `ioc-${idx}`,
        container_code: String(r?.container_code || r?.target_label || '').trim(),
        output_code: String(r?.output_code || r?.container_code || r?.target_label || '').trim(),
        display_code: String(r?.display_code || r?.output_code || r?.container_code || r?.target_label || '').trim(),
        instruction_id: Number(r?.instruction_id || r?.prev_instruction_id || 0) || undefined,
        instruction_code: String(r?.instruction_code || '').trim() || undefined,
        project_id: Number(r?.project_id || 0) || undefined,
      }));
      if (baseRows.length === 0) {
        if (!cancelled) setEnrichedRows([]);
        return;
      }
      let mergedRows = baseRows;

      try {
        const prevIds: number[] = Array.from(new Set(
          (baseRows || [])
            .map((x: any) => Number(x?.instruction_id))
            .filter((v: number) => Number.isFinite(v) && v > 0)
        ));
        if (prevIds.length > 0) {
          const detailByCode = new Map<string, any>();
          const detailById = new Map<string, any>();
          await Promise.all(prevIds.map(async (pid: number) => {
            try {
              const rr = await request.get(`/api/v1/instruction/instructions/${pid}/ioc/with-contents/`);
              const arr = Array.isArray(rr?.data?.results) ? rr.data.results : [];
              (arr || []).forEach((oc: any) => {
                const idKey = `${pid}::id::${String(oc?.id || '')}`;
                const codeKey = `${pid}::code::${normalizeIocCode(oc?.code)}`;
                if (String(oc?.id || '').trim()) detailById.set(idKey, oc);
                if (normalizeIocCode(oc?.code)) detailByCode.set(codeKey, oc);
              });
            } catch {}
          }));
          mergedRows = (baseRows || []).map((row: any) => {
            const pid = Number(row?.instruction_id);
            if (!Number.isFinite(pid) || pid <= 0) return row;
            const byId = row?.output_container_id ? detailById.get(`${pid}::id::${String(row.output_container_id)}`) : undefined;
            const byCode = detailByCode.get(`${pid}::code::${normalizeIocCode(row?.output_code || row?.display_code || row?.container_code)}`);
            const hit = byId || byCode;
            if (!hit) return row;
            const contents = Array.isArray(hit?.contents) ? hit.contents : [];
            const totalQty = contents.reduce((acc: number, c: any) => acc + (Number(c?.quantity || 0) || 0), 0);
            const kind = String(hit?.content_kind || row?.output_content_kind || '').trim().toLowerCase();
            const detailText = (contents || []).map((c: any) => {
              const code = String(c?.inventory_unit_system_barcode || c?.inventory_unit_id || '').trim();
              const qty = Number(c?.quantity || 0) || 0;
              return code ? `${code}×${qty}` : '';
            }).filter(Boolean).join('；');
            return {
              ...row,
              output_container_id: hit?.id ?? row?.output_container_id,
              output_code: String(hit?.code || row?.output_code || row?.display_code || row?.container_code || '').trim(),
              display_code: String(hit?.code || row?.display_code || row?.output_code || row?.container_code || '').trim(),
              container_type_display: row?.container_type_display && String(row.container_type_display).trim() !== '-'
                ? row.container_type_display
                : (String(hit?.container_type || '').trim() || '-'),
              output_content_kind: kind || row?.output_content_kind || '',
              output_content_kind_display: row?.output_content_kind_display && String(row.output_content_kind_display).trim() !== '-'
                ? row.output_content_kind_display
                : (kind === 'exception' ? '异常内容容器' : '正常内容容器'),
              output_contents_summary: row?.output_contents_summary && String(row.output_contents_summary).trim() !== '-'
                ? row.output_contents_summary
                : (contents.length > 0 ? `${contents.length}项` : '-'),
              output_contents_detail: String(row?.output_contents_detail || '').trim() || detailText,
              output_contents_total_qty: Number(row?.output_contents_total_qty || 0) > 0 ? Number(row.output_contents_total_qty || 0) : totalQty,
              length_cm: hit?.length_cm ?? row?.length_cm,
              width_cm: hit?.width_cm ?? row?.width_cm,
              height_cm: hit?.height_cm ?? row?.height_cm,
              weight_g: hit?.weight_g ?? row?.weight_g,
            };
          });
        }
      } catch {}

      try {
        const projectIds: number[] = Array.from(new Set(
          (mergedRows || [])
            .map((r: any) => Number(r?.project_id))
            .filter((v: number) => Number.isFinite(v) && v > 0)
        ));
        if (projectIds.length > 0) {
          const currentInstructionId = Number(data?.instruction_id || 0) || undefined;
          const unifiedByCode = new Map<string, any>();
          await Promise.all(projectIds.map(async (pid: number) => {
            try {
              const params: any = {
                page: 1,
                page_size: 500,
                container_kind: 'output',
                project: String(pid),
                ordering: '-created_at',
              };
              if (currentInstructionId) params.instruction_id = currentInstructionId;
              const resp = await request.get('/api/v1/storage/containers/unified/', { params });
              const list = Array.isArray(resp?.data?.results) ? resp.data.results : [];
              (list || []).forEach((x: any) => {
                const code = normalizeIocCode(x?.system_code || x?.display_code || '');
                if (code) unifiedByCode.set(`${pid}::${code}`, x);
              });
            } catch {}
          }));
          mergedRows = (mergedRows || []).map((row: any) => {
            const pid = Number(row?.project_id);
            const code = normalizeIocCode(row?.output_code || row?.display_code || row?.container_code);
            if (!Number.isFinite(pid) || pid <= 0 || !code) return row;
            const hit = unifiedByCode.get(`${pid}::${code}`);
            if (!hit) return row;
            return {
              ...row,
              output_code: String(hit?.system_code || hit?.display_code || row?.output_code || row?.display_code || '').trim(),
              display_code: String(hit?.display_code || hit?.system_code || row?.display_code || row?.output_code || '').trim(),
              container_type_display: hit?.container_type_display || row?.container_type_display || '-',
              output_content_kind: hit?.output_content_kind || row?.output_content_kind || '',
              output_content_kind_display: hit?.output_content_kind_display || row?.output_content_kind_display || '-',
              output_contents_summary: hit?.output_contents_summary || row?.output_contents_summary || '-',
              output_contents_detail: hit?.output_contents_detail || row?.output_contents_detail || '',
              output_contents_total_qty: Number(hit?.output_contents_total_qty || row?.output_contents_total_qty || 0) || 0,
              length_cm: hit?.length_cm ?? row?.length_cm,
              width_cm: hit?.width_cm ?? row?.width_cm,
              height_cm: hit?.height_cm ?? row?.height_cm,
              weight_g: hit?.weight_g ?? row?.weight_g,
            };
          });
        }
      } catch {}

      try {
        const needIds: number[] = Array.from(new Set(
          (mergedRows || [])
            .filter((r: any) => !String(r?.instruction_code || '').trim())
            .map((r: any) => Number(r?.instruction_id || 0))
            .filter((id: number) => Number.isFinite(id) && id > 0)
        ));
        if (needIds.length > 0) {
          const codeMap: Record<number, string> = {};
          await Promise.all(needIds.map(async (id: number) => {
            try {
              const resp = await request.get(`/api/v1/instruction/instructions/${id}/`);
              const d = resp?.data || {};
              codeMap[id] = String(d?.system_code || d?.code || '').trim();
            } catch {}
          }));
          mergedRows = (mergedRows || []).map((r: any) => {
            const id = Number(r?.instruction_id || 0);
            if (!id || !codeMap[id]) return r;
            if (String(r?.instruction_code || '').trim()) return r;
            return { ...r, instruction_code: codeMap[id] };
          });
        }
      } catch {}

      if (!cancelled) setEnrichedRows(mergedRows);
    };
    void loadIocRows();
    return () => { cancelled = true; };
  }, [rows, data?.instruction_id]);

  const doneSet = useMemo(() => {
    const s = new Set<string>();
    (pickLogs || []).forEach((r) => {
      const code = String(r?.container_code || '').trim();
      if (code) s.add(code);
    });
    return s;
  }, [pickLogs]);

  const sourceRows = useMemo(() => (Array.isArray(enrichedRows) && enrichedRows.length > 0 ? enrichedRows : rows), [enrichedRows, rows]);
  const hierarchySortedRows = useMemo(() => {
    const list = (sourceRows || []).map((r: any, idx: number) => {
      const codeRaw = String(r?.output_code || r?.display_code || r?.container_code || r?.target_label || '').trim();
      const code = normalizeIocCode(codeRaw);
      const parentRaw = String(
        r?.parent_container_code
        || r?.parent_code
        || r?.parent_output_code
        || r?.parent_display_code
        || '',
      ).trim();
      const parentCode = normalizeIocCode(parentRaw);
      return {
        ...r,
        __source_idx: idx,
        __ioc_code_norm: code,
        __parent_code_norm: parentCode,
      };
    });
    const childCountByCode = new Map<string, number>();
    list.forEach((r: any) => {
      const p = String(r?.__parent_code_norm || '').trim();
      if (!p) return;
      childCountByCode.set(p, (childCountByCode.get(p) || 0) + 1);
    });
    const groupFirstIdx = new Map<string, number>();
    const grouped = new Map<string, any[]>();
    list.forEach((r: any) => {
      const code = String(r?.__ioc_code_norm || '').trim();
      const parentCode = String(r?.__parent_code_norm || '').trim();
      const hasChildren = code ? (Number(childCountByCode.get(code) || 0) > 0) : false;
      const role: 'parent' | 'child' | 'standalone' = parentCode ? 'child' : (hasChildren ? 'parent' : 'standalone');
      const groupKey = role === 'standalone' ? `standalone:${code || r?.__source_idx}` : `hier:${parentCode || code}`;
      if (!groupFirstIdx.has(groupKey)) groupFirstIdx.set(groupKey, Number(r?.__source_idx || 0));
      const arr = grouped.get(groupKey) || [];
      arr.push({ ...r, __hierarchy_role: role, __group_key: groupKey });
      grouped.set(groupKey, arr);
    });
    const orderedGroupKeys = Array.from(grouped.keys()).sort((a, b) => {
      const ia = Number(groupFirstIdx.get(a) || 0);
      const ib = Number(groupFirstIdx.get(b) || 0);
      if (ia !== ib) return ia - ib;
      return String(a).localeCompare(String(b), undefined, { sensitivity: 'base' });
    });
    const roleOrder = (role: string): number => (role === 'parent' ? 0 : (role === 'child' ? 1 : 2));
    const out: any[] = [];
    orderedGroupKeys.forEach((gk) => {
      const rowsInGroup = (grouped.get(gk) || []).slice().sort((a: any, b: any) => {
        const ra = roleOrder(String(a?.__hierarchy_role || 'standalone'));
        const rb = roleOrder(String(b?.__hierarchy_role || 'standalone'));
        if (ra !== rb) return ra - rb;
        const ca = String(a?.__ioc_code_norm || '');
        const cb = String(b?.__ioc_code_norm || '');
        if (ca && cb && ca !== cb) return ca.localeCompare(cb, undefined, { numeric: true, sensitivity: 'base' });
        return Number(a?.__source_idx || 0) - Number(b?.__source_idx || 0);
      });
      out.push(...rowsInGroup);
    });
    return out.map((r: any) => {
      const { __source_idx, __ioc_code_norm, __parent_code_norm, ...rest } = r;
      return rest;
    });
  }, [sourceRows]);

  const displayRows = useMemo(() => {
    return (hierarchySortedRows || []).map((r: any, idx: number) => {
      const code = String(r?.container_code || r?.target_label || '').trim();
      const picked = doneSet.has(code);
      return {
        ...r,
        __idx: idx + 1,
        row_id: r?.row_id || `ioc-${idx}`,
        target_label: code || '-',
        container_code: code || '-',
        target_qty: 1,
        picked_qty: picked ? 1 : 0,
        diff_qty: picked ? 0 : 1,
        done: picked,
      };
    });
  }, [hierarchySortedRows, doneSet]);

  const groupingMeta = useMemo(() => {
    const rowToGroup = new Map<string, string>();
    const roleByRow = new Map<string, 'parent' | 'child' | 'standalone'>();
    const groupCount = new Map<string, number>();
    (displayRows || []).forEach((r: any) => {
      const rowKey = String(r?.row_id || r?.container_code || '');
      if (!rowKey) return;
      const role = (String(r?.__hierarchy_role || '').trim() === 'parent'
        ? 'parent'
        : (String(r?.__hierarchy_role || '').trim() === 'child' ? 'child' : 'standalone')) as ('parent' | 'child' | 'standalone');
      const code = normalizeIocCode(r?.output_code || r?.display_code || r?.container_code || r?.target_label || '');
      const groupKey = String(r?.__group_key || (role === 'standalone' ? `standalone:${code || rowKey}` : `hier:${code || rowKey}`));
      rowToGroup.set(rowKey, groupKey);
      roleByRow.set(rowKey, role);
      groupCount.set(groupKey, (groupCount.get(groupKey) || 0) + 1);
    });
    return { rowToGroup, roleByRow, groupCount };
  }, [displayRows]);

  const rowGroupBorderMap = useMemo(() => {
    const m = new Map<string, { grouped: boolean; top: boolean; bottom: boolean }>();
    (displayRows || []).forEach((r: any, idx: number) => {
      const rowKey = String(r?.row_id || r?.container_code || '');
      if (!rowKey) return;
      const groupKey = String(groupingMeta.rowToGroup.get(rowKey) || '');
      const prevRowKey = idx > 0 ? String(displayRows[idx - 1]?.row_id || displayRows[idx - 1]?.container_code || '') : '';
      const nextRowKey = idx < (displayRows.length - 1) ? String(displayRows[idx + 1]?.row_id || displayRows[idx + 1]?.container_code || '') : '';
      const prevGroup = prevRowKey ? String(groupingMeta.rowToGroup.get(prevRowKey) || '') : '';
      const nextGroup = nextRowKey ? String(groupingMeta.rowToGroup.get(nextRowKey) || '') : '';
      const grouped = groupKey.startsWith('hier:') && Number(groupingMeta.groupCount.get(groupKey) || 0) > 1;
      m.set(rowKey, {
        grouped,
        top: grouped && prevGroup !== groupKey,
        bottom: grouped && nextGroup !== groupKey,
      });
    });
    return m;
  }, [displayRows, groupingMeta]);

  const stats = useMemo(() => {
    const total = displayRows.length;
    const picked = displayRows.filter((r: any) => !!r?.done).length;
    return { total, picked, todo: Math.max(total - picked, 0) };
  }, [displayRows]);
  const boardRate = useMemo(() => {
    const targetTotal = Number(stats?.total ?? 0);
    const pickedTotal = Number(stats?.picked ?? 0);
    const rateValue = targetTotal > 0 ? (pickedTotal / targetTotal) * 100 : null;
    const rateText = (rateValue == null || !Number.isFinite(rateValue)) ? '-' : `${Number(rateValue.toFixed(1))}%`;
    return {
      rateValue: (rateValue == null || !Number.isFinite(rateValue)) ? null : Number(rateValue),
      rateText,
    };
  }, [stats]);
  const allPicked = useMemo(() => stats.total > 0 && stats.todo === 0, [stats.total, stats.todo]);
  const leftFilteredRows = useMemo(() => {
    if (!hideCompleted) return displayRows;
    return displayRows.filter((r: any) => !r?.done);
  }, [displayRows, hideCompleted]);
  const warehouseLocationCount = useMemo(() => {
    const set = new Set<string>();
    (displayRows || []).forEach((r: any) => {
      const loc = String(r?.location_code || '').trim();
      if (loc && loc !== '-') set.add(loc);
    });
    return set.size;
  }, [displayRows]);
  const leftPagedRows = useMemo(() => {
    const start = (leftPage - 1) * leftPageSize;
    return leftFilteredRows.slice(start, start + leftPageSize);
  }, [leftFilteredRows, leftPage, leftPageSize]);
  const logPagedRows = useMemo(() => {
    const start = (logPage - 1) * logPageSize;
    return (pickLogs || []).slice(start, start + logPageSize);
  }, [pickLogs, logPage, logPageSize]);
  useEffect(() => {
    const maxLeft = Math.max(1, Math.ceil(leftFilteredRows.length / leftPageSize));
    if (leftPage > maxLeft) setLeftPage(1);
  }, [leftFilteredRows.length, leftPageSize, leftPage]);
  useEffect(() => {
    const maxLog = Math.max(1, Math.ceil((pickLogs || []).length / logPageSize));
    if (logPage > maxLog) setLogPage(1);
  }, [pickLogs, logPageSize, logPage]);

  const addPickLog = (containerCode: string) => {
    const playAlertBeep = () => {
      try {
        const AudioCtx = (window as any).AudioContext || (window as any).webkitAudioContext;
        if (!AudioCtx) return;
        const ctx = new AudioCtx();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'square';
        osc.frequency.value = 880;
        gain.gain.setValueAtTime(0.0001, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.12, ctx.currentTime + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.18);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start();
        osc.stop(ctx.currentTime + 0.2);
      } catch {}
    };
    const code = String(containerCode || '').trim();
    if (!code) return;
    if (doneSet.has(code)) {
      message.info(`IOC ${code} 已拣出`);
      playAlertBeep();
      return;
    }
    const exists = displayRows.some((r: any) => String(r?.container_code || '').trim() === code);
    if (!exists) {
      message.warning(`未匹配到IOC目标：${code}`);
      playAlertBeep();
      return;
    }
    const now = new Date();
    const ts = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;
    setPickLogs((prev) => ([
      ...(prev || []),
      {
        id: `ioc-${code}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        container_code: code,
        // 与现有提交流程兼容：IOC 模式下 target_code 以 container_code 占位
        target_code: code,
        qty: 1,
        off_shelf: true,
        operated_at: ts,
      },
    ]));
  };

  const removePickLog = (containerCode: string) => {
    const code = String(containerCode || '').trim();
    if (!code) return;
    setPickLogs((prev) => {
      const list = Array.isArray(prev) ? prev : [];
      const idx = list.findIndex((x) => String(x?.container_code || '').trim() === code);
      if (idx < 0) return prev;
      return [...list.slice(0, idx), ...list.slice(idx + 1)];
    });
  };

  const pickAll = () => {
    (displayRows || []).forEach((r: any) => {
      const code = String(r?.container_code || '').trim();
      if (code) addPickLog(code);
    });
  };
  const onPrintWarehouseTable = () => {
    try {
      const rowsToPrint = Array.isArray(displayRows) ? displayRows : [];
      const htmlRows = rowsToPrint.map((r: any, idx: number) => `
        <tr>
          <td>${idx + 1}</td>
          <td>${String(r?.output_code || r?.display_code || r?.container_code || '-')}</td>
          <td>${String(r?.container_type_display || '-')}</td>
          <td>${String(r?.location_code || '-')}</td>
          <td></td>
        </tr>
      `).join('');
      const w = window.open('', '_blank');
      if (!w) return;
      w.document.write(`
        <!doctype html>
        <html>
          <head>
            <meta charset="utf-8" />
            <title>仓位表打印</title>
            <style>
              @page { size: 4in 6in; margin: 0.2in; }
              html, body { width: 4in; height: 6in; margin: 0; padding: 0; }
              body { font-family: Arial, "Microsoft YaHei", sans-serif; box-sizing: border-box; padding: 0.1in; font-weight: 700; }
              .header { display: flex; align-items: center; justify-content: space-between; margin: 0 0 10px 0; }
              h2 { margin: 0; font-size: 16px; line-height: 1.2; font-weight: 700; }
              .meta { font-size: 11px; color: #595959; text-align: right; line-height: 1.4; font-weight: 700; }
              table { width: 100%; border-collapse: collapse; }
              th, td { border: 1px solid #d9d9d9; padding: 6px 8px; text-align: left; font-size: 12px; font-weight: 700; }
              th { background: #fafafa; font-weight: 700; }
            </style>
          </head>
          <body>
            <div class="header">
              <h2>仓位表</h2>
              <div class="meta">
                <div>指令：${String(instructionCodeFromParent || '-')}</div>
                <div>IOC总数量：${rowsToPrint.length}</div>
              </div>
            </div>
            <table>
              <thead>
                <tr>
                  <th>序号</th>
                  <th>IOC系统编码</th>
                  <th>容器类型</th>
                  <th>仓位</th>
                  <th>已拣出</th>
                </tr>
              </thead>
              <tbody>${htmlRows}</tbody>
            </table>
          </body>
        </html>
      `);
      w.document.close();
      w.focus();
      try {
        w.onafterprint = () => {
          try { w.close(); } catch {}
        };
      } catch {}
      w.print();
      setTimeout(() => {
        try { if (!w.closed) w.close(); } catch {}
      }, 1200);
    } catch {}
  };

  const clearAll = () => {
    setPickLogs([]);
  };

  const onScanSubmit = () => {
    const code = String(scanText || '').trim();
    if (!code) return;
    addPickLog(code);
    setScanText('');
    try { scanRef.current?.focus(); } catch {}
  };

  // 暴露给父级提交汇总逻辑
  useEffect(() => {
    try {
      (window as any).__PICK_LOGS__ = Array.isArray(pickLogs) ? pickLogs : [];
    } catch {}
  }, [pickLogs]);

  // 初始化：优先加载后端 draft（IOC 与库存共用草稿接口）
  useEffect(() => {
    (async () => {
      try {
        if (!taskId) return;
        const resp = await request.get(`${base}/${taskId}/picking-draft/`);
        const draft = resp?.data?.draft || {};
        const dStage = Number.isFinite(Number(draft?.stage_idx)) ? Number(draft.stage_idx) : null;
        const curStage = Number.isFinite(Number(stageNo)) ? Number(stageNo) : null;
        if (curStage != null && dStage != null && curStage !== dStage) return;
        const logs = Array.isArray(draft?.pick_logs) ? draft.pick_logs : [];
        if (logs.length > 0) setPickLogs(logs);
      } catch {}
    })();
  }, [taskId, stageNo]);

  // 自动保存草稿（简化版）
  const saveTimerRef = useRef<any>(null);
  useEffect(() => {
    if (!taskId || isReadonly) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      try {
        await request.post(`${base}/${taskId}/picking-draft/`, {
          stage_idx: (Number.isFinite(Number(stageNo)) ? Number(stageNo) : null),
          pick_logs: pickLogs,
          stats: {
            total_qty: (pickLogs || []).reduce((s, r) => s + (Number(r?.qty || 0) || 0), 0),
            kinds: new Set((pickLogs || []).map((r) => String(r?.target_code || '').trim()).filter(Boolean)).size,
            containers: new Set((pickLogs || []).map((r) => String(r?.container_code || '').trim()).filter(Boolean)).size,
            target_total: (rows || []).length,
            picked_total: (pickLogs || []).length,
            target_scope: 'instruction_output_container',
            execution_session_id: String(executionSessionId || '').trim() || undefined,
          },
        });
      } catch {}
    }, 600);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [pickLogs, taskId, stageNo, isReadonly, rows, executionSessionId]);

  const openInstructionDetail = (instId: number) => {
    try {
      if (!Number.isFinite(instId) || instId <= 0) return;
      const w: any = window as any;
      const path = String(w?.location?.pathname || '');
      const m = path.match(/^\/dashboard\/(admin|staff|customer)(?:\/|$)/);
      const role = m?.[1] || 'customer';
      const detailUrl = `/dashboard/${role}/instructions/i-${instId}`;
      if (w.tabsManager && typeof w.tabsManager.openTabByPath === 'function') {
        w.tabsManager.openTabByPath(detailUrl);
        return;
      }
      w.open(detailUrl, '_blank');
    } catch {}
  };
  const leftColumns: any[] = [
    { title: '序号', dataIndex: '__idx', width: 70, align: 'center' },
    {
      title: 'IOC系统编码',
      dataIndex: 'output_code',
      width: 180,
      render: (v: any, r: any) => {
        const code = String(v || r?.display_code || r?.container_code || '-');
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{code}</span>
            {code && code !== '-' ? (
              <CopyOutlined
                style={{ color: '#1677ff', cursor: 'pointer' }}
                onClick={async (e) => {
                  e.stopPropagation();
                  try {
                    await navigator.clipboard.writeText(code);
                    message.success('已复制系统编码');
                  } catch {
                    message.error('复制失败');
                  }
                }}
              />
            ) : null}
          </span>
        );
      },
    },
    { title: '仓位', dataIndex: 'location_code', width: 110, align: 'center', render: (v: any) => String(v || '-') },
    { title: '状态', dataIndex: 'done', width: 110, align: 'center', render: (v: any) => (v ? <Tag color="success">已拣出</Tag> : <Tag color="default">待拣出</Tag>) },
    {
      title: '来源历史指令',
      dataIndex: 'instruction_code',
      width: 180,
      render: (_: any, r: any) => {
        const instId = Number(r?.instruction_id || 0);
        const text = String(r?.instruction_code || (instId > 0 ? `#${instId}` : '-')).trim() || '-';
        if (!Number.isFinite(instId) || instId <= 0) return <span style={{ fontFamily: 'monospace' }}>{text}</span>;
        return (
          <Button
            type="link"
            size="small"
            style={{ padding: 0, fontFamily: 'monospace' }}
            onClick={(e) => { e.stopPropagation(); openInstructionDetail(instId); }}
          >
            {text}
          </Button>
        );
      },
    },
    { title: '容器类型', dataIndex: 'container_type_display', width: 130, render: (v: any) => String(v || '-') },
    {
      title: '层级标识',
      key: '__hierarchy_role__',
      width: 120,
      render: (_: any, r: any) => {
        const rowKey = String(r?.row_id || r?.container_code || '');
        const role = groupingMeta.roleByRow.get(rowKey) || 'standalone';
        if (role === 'standalone') return <span style={{ color: '#999' }}>-</span>;
        const roleText = role === 'parent' ? '父容器' : '子容器';
        const roleStyle = role === 'parent'
          ? { color: '#1677ff', border: '#91caff', bg: '#e6f4ff' }
          : { color: '#d4380d', border: '#ffbb96', bg: '#fff2e8' };
        return (
          <span
            style={{
              display: 'inline-block',
              minWidth: 76,
              textAlign: 'center',
              padding: '2px 10px',
              borderRadius: 6,
              border: `1px solid ${roleStyle.border}`,
              color: roleStyle.color,
              background: roleStyle.bg,
              lineHeight: 1.4,
            }}
          >
            {roleText}
          </span>
        );
      },
    },
    {
      title: '内容类型',
      dataIndex: 'output_content_kind_display',
      width: 130,
      render: (v: any, r: any) => {
        const kind = String(r?.output_content_kind || '').toLowerCase();
        const isException = kind === 'exception';
        return (
          <span
            style={{
              display: 'inline-block',
              padding: '2px 8px',
              borderRadius: 4,
              border: `1px solid ${isException ? '#ffa940' : '#73d13d'}`,
              background: isException ? '#fff7e6' : '#f6ffed',
              color: isException ? '#fa8c16' : '#389e0d',
              lineHeight: 1.5,
            }}
          >
            {String(v || '-')}
          </span>
        );
      },
    },
    {
      title: '容器内容统计',
      dataIndex: 'output_contents_summary',
      width: 150,
      render: (_: any, r: any) => {
        const kind = String(r?.output_content_kind || '').toLowerCase();
        const qty = Number(r?.output_contents_total_qty || 0);
        if (kind === 'exception') return `${Number.isFinite(qty) ? qty : 0}件`;
        return String(r?.output_contents_summary || '-');
      },
    },
    {
      title: '容器内容',
      dataIndex: 'output_contents_detail',
      width: 240,
      render: (v: any, r: any) => {
        const kind = String(r?.output_content_kind || '').toLowerCase();
        if (kind === 'exception') {
          return (
            <Tooltip title="无法操作等异常产品">
              <div style={{ color: '#595959', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                无法操作等异常产品
              </div>
            </Tooltip>
          );
        }
        const full = String(v || '').trim();
        if (!full) return '-';
        const lines = full.split(/[；;]/).map((x) => String(x || '').trim()).filter(Boolean);
        if (lines.length === 0) return '-';
        const preview = lines.slice(0, 3);
        const hasMore = lines.length > 3;
        return (
          <div style={{ maxWidth: 300 }}>
            {preview.map((line, idx) => (
              <div key={`${line}-${idx}`} style={{ position: 'relative', minHeight: 20, marginBottom: idx === preview.length - 1 ? 0 : 2 }}>
                <Tooltip title={line}>
                  <div style={{ color: '#595959', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: hasMore && idx === preview.length - 1 ? 56 : 0 }}>
                    {line}
                  </div>
                </Tooltip>
                {hasMore && idx === preview.length - 1 ? (
                  <Button
                    type="link"
                    size="small"
                    style={{ position: 'absolute', right: 0, bottom: -2, padding: 0, height: 'auto', lineHeight: 1.2 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      const iocCode = String(r?.output_code || r?.display_code || '-');
                      Modal.info({
                        title: `容器内容完整记录 - ${iocCode}`,
                        width: 760,
                        centered: true,
                        content: (
                          <div style={{ maxHeight: 420, overflowY: 'auto' }}>
                            {lines.map((x, i) => (
                              <div key={`${x}-${i}`} style={{ marginBottom: 6, wordBreak: 'break-all' }}>
                                {i + 1}. {x}
                              </div>
                            ))}
                          </div>
                        ),
                      });
                    }}
                  >
                    [...]
                  </Button>
                ) : null}
              </div>
            ))}
          </div>
        );
      },
    },
    { title: '容器尺寸', key: 'dims', width: 130, render: (_: any, r: any) => (r?.length_cm != null && r?.width_cm != null && r?.height_cm != null) ? `${r.length_cm}×${r.width_cm}×${r.height_cm} cm` : '-' },
    { title: '容器重量', dataIndex: 'weight_g', width: 100, render: (v: any) => (v != null ? `${v}g` : '-') },
    {
      title: '操作',
      key: 'op',
      width: 110,
      align: 'center',
      render: (_: any, r: any) => {
        const code = String(r?.container_code || '').trim();
        const picked = !!r?.done;
        return picked ? (
          <Button
            size="small"
            onClick={() => removePickLog(code)}
            disabled={isReadonly}
            style={{ background: '#fa541c', borderColor: '#fa541c', color: '#ffffff' }}
          >
            撤销
          </Button>
        ) : (
          <Button size="small" type="primary" onClick={() => addPickLog(code)} disabled={isReadonly}>拣出</Button>
        );
      },
    },
  ];

  const logColumns: any[] = [
    { title: '时间', dataIndex: 'operated_at', width: 180, render: (v: any) => String(v || '-') },
    { title: 'IOC容器编码', dataIndex: 'container_code', render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
    { title: '动作', key: 'kind', width: 100, align: 'center', render: () => <Tag color="blue">整容器拣出</Tag> },
    {
      title: '操作',
      key: 'op',
      width: 100,
      align: 'center',
      render: (_: any, r: any) => (
        <Button size="small" danger onClick={() => removePickLog(String(r?.container_code || ''))} disabled={isReadonly}>删除</Button>
      ),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <style>{`
        .ioc-pick-table .ant-table-tbody > tr.ioc-row-picked > td { background: #f6ffed !important; }
        .ioc-pick-table .ant-table-tbody > tr.ioc-group-row > td {
          border-left: 2px solid #91caff !important;
          border-right: 2px solid #91caff !important;
        }
        .ioc-pick-table .ant-table-tbody > tr.ioc-group-start > td {
          border-top: 2px solid #91caff !important;
        }
        .ioc-pick-table .ant-table-tbody > tr.ioc-group-end > td {
          border-bottom: 2px solid #91caff !important;
        }
      `}</style>
      <div style={{ background: '#ffffff', border: '1px solid #f0f0f0', borderRadius: 6, padding: 8 }}>
        <div
          style={{
            width: '100%',
            background: '#f6fffb',
            borderRadius: 6,
            overflow: 'hidden',
            opacity: isReadonly ? 0.55 : 1,
            pointerEvents: isReadonly ? 'none' as any : 'auto',
            filter: isReadonly ? 'grayscale(0.15)' : undefined,
          }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'stretch', gap: 12, margin: '0 0 8px 0', minHeight: 34 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
              <span style={{ fontWeight: 600 }}>仓位表与拣货进度看板</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 13, color: '#595959' }}>仅显示未完成记录</span>
                <Switch checked={hideCompleted} onChange={setHideCompleted} size="small" />
              </span>
              <Tag
                style={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => {
                  Modal.info({
                    title: 'IOC整容器拣货模式说明',
                    content: (
                      <div>
                        <div>IOC整容器拣货模式</div>
                        <div>每个IOC容器作为一个不可拆分目标；不支持部分拣出，点击“拣出”或扫码后即视为整容器拣出。</div>
                      </div>
                    ),
                    width: 560,
                    okText: '知道了',
                  });
                }}
              >
                ?
              </Tag>
              <Button
                size="small"
                onClick={onPrintWarehouseTable}
                style={{ marginLeft: 8, background: '#fa8c16', borderColor: '#fa8c16', color: '#ffffff' }}
              >
                打印
              </Button>
              <Button
                size="small"
                onClick={pickAll}
                disabled={isReadonly || stats.todo === 0}
                style={{ background: '#1677ff', borderColor: '#1677ff', color: '#ffffff' }}
              >
                全部拣出
              </Button>
            </div>
            <div style={{ display: 'flex', alignItems: 'stretch', justifyContent: 'center' }}>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '1px 6px',
                  border: (boardRate.rateValue != null && boardRate.rateValue >= 100) ? '1px solid #b7eb8f' : '1px solid #ffadd2',
                  background: (boardRate.rateValue != null && boardRate.rateValue >= 100) ? '#f6ffed' : '#fff0f6',
                  color: (boardRate.rateValue != null && boardRate.rateValue >= 100) ? '#389e0d' : '#c41d7f',
                  borderRadius: 4,
                  fontWeight: 700,
                  fontSize: 12,
                  lineHeight: '18px',
                }}
              >
                <span>完成率 {boardRate.rateText}</span>
                {(boardRate.rateValue != null && boardRate.rateValue >= 100) ? (
                  <span style={{ color: '#389e0d', fontWeight: 900, fontSize: 18, lineHeight: '18px' }}>√</span>
                ) : null}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
              <Tag color={stats.todo === 0 ? 'success' : 'warning'}>{`目标IOC ${stats.total}`}</Tag>
              <Tag color={allPicked ? 'success' : (warehouseLocationCount > 0 ? 'default' : 'warning')}>{`涉及仓位 ${warehouseLocationCount}`}</Tag>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  height: 22,
                  padding: '0 8px',
                  borderRadius: 4,
                  background: '#f6ffed',
                  border: '1px solid #b7eb8f',
                  color: '#389e0d',
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                {`已拣出 ${stats.picked}`}
              </span>
              <Tag color={stats.todo === 0 ? 'success' : 'error'}>{`待拣出 ${stats.todo}`}</Tag>
            </div>
          </div>
          <div style={{ width: '100%', overflowX: 'auto' }}>
            <Table
              className="ioc-pick-table"
              size="small"
              rowKey={(r: any) => String(r?.row_id || r?.container_code || '')}
              dataSource={leftPagedRows}
              columns={leftColumns}
              rowClassName={(record: any) => {
                const rowKey = String(record?.row_id || record?.container_code || '');
                const borderMeta = rowGroupBorderMap.get(rowKey) || { grouped: false, top: false, bottom: false };
                const classes: string[] = [];
                if (record?.done) classes.push('ioc-row-picked');
                if (borderMeta.grouped) {
                  if (borderMeta.top) classes.push('ioc-group-row', 'ioc-group-start');
                  else if (borderMeta.bottom) classes.push('ioc-group-row', 'ioc-group-end');
                  else classes.push('ioc-group-row');
                }
                return classes.join(' ');
              }}
              pagination={false}
              scroll={{ x: 'max-content' }}
              locale={{ emptyText: hideCompleted ? '暂无未完成IOC目标' : '暂无IOC目标' }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
              <Pagination
                size="small"
                current={leftPage}
                pageSize={leftPageSize}
                total={leftFilteredRows.length}
                showSizeChanger
                showQuickJumper
                onChange={(p, ps) => {
                  setLeftPage(p);
                  setLeftPageSize(ps);
                }}
                showTotal={(total) => `共 ${total} 条`}
              />
            </div>
          </div>
        </div>
      </div>

      <div style={{ background: '#ffffff', border: '1px solid #f0f0f0', borderRadius: 6, padding: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <div style={{ fontWeight: 600 }}>拣货操作记录</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <Tag color={stats.todo === 0 ? 'success' : 'warning'}>{`目标IOC ${stats.total}`}</Tag>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                height: 22,
                padding: '0 8px',
                borderRadius: 4,
                background: '#f6ffed',
                border: '1px solid #b7eb8f',
                color: '#389e0d',
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              {`已拣出 ${stats.picked}`}
            </span>
            <Tag color={stats.todo === 0 ? 'success' : 'error'}>{`待拣出 ${stats.todo}`}</Tag>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', opacity: isReadonly ? 0.55 : 1, pointerEvents: isReadonly ? 'none' as any : 'auto', filter: isReadonly ? 'grayscale(0.15)' : undefined }}>
          <Input
            ref={scanRef}
            style={{ width: 320 }}
            value={scanText}
            onChange={(e) => setScanText(e.target.value)}
            onPressEnter={onScanSubmit}
            placeholder="扫描/输入IOC容器编码后回车"
            disabled={isReadonly}
          />
          <Button
            onClick={onScanSubmit}
            disabled={isReadonly}
            style={{ background: '#1677ff', borderColor: '#1677ff', color: '#ffffff' }}
          >
            扫码拣出
          </Button>
          <Button danger onClick={clearAll} disabled={isReadonly || pickLogs.length === 0}>清空记录</Button>
        </div>
        <div style={{ marginTop: 12 }}>
          <Table
            size="small"
            rowKey={(r: any) => String(r?.id || `${r?.container_code}-${r?.operated_at}`)}
            dataSource={logPagedRows}
            columns={logColumns}
            pagination={false}
            locale={{ emptyText: '暂无拣货记录' }}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
            <Pagination
              size="small"
              current={logPage}
              pageSize={logPageSize}
              total={(pickLogs || []).length}
              showSizeChanger
              showQuickJumper
              onChange={(p, ps) => {
                setLogPage(p);
                setLogPageSize(ps);
              }}
              showTotal={(total) => `共 ${total} 条`}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default IocPickStepRenderer;
